/**
 * IGD Custom Function — InSync Guidance Data.
 *
 * Fetches the
 * ticker's guidance table (consensus/guidance_with_actuals_estimates_deltas),
 * finds the row for the requested line item + fiscal period (and optional source
 * period), and returns the guide value for the requested category:
 *
 *   =INSYNC.IGD_OFJ(ticker, fiscalPeriod, ltCode, category [, sourcePeriod])
 *
 *   category:  G -> GUIDE   GH -> HIGH_GUIDE   GM -> MID_GUIDE   GL -> LOW_GUIDE
 *
 * Empty result -> the null-return value ("-"), matching the COM add-in default.
 */

import { api } from '../core/api/endpoints';
import { tokenStorage } from '../core/auth/tokenStorage';
import { recordAddinActivity } from '../core/auth/idleSession';
import { createLogger } from '../utils/logger';

const logger = createLogger('IGD');

const NULL_RETURN_VALUE = '-';

// IGD always reads the new-guidance columns (GUIDE/LOW_GUIDE/…); the COM
// useNewGuidance flag only selects the endpoint, and the new flow is the one in
// use (mirrors InsertDataPane's hardcoded useNewGuidance = true).
const USE_NEW_GUIDANCE = true;

// Per-ticker guidance rows cache, mirroring the COM DataStoreGuidance-<ticker>
// cache: one fetch per ticker/token, reused across every IGD cell.
const guidanceCache = new Map<string, Promise<Record<string, any>[]>>();

/** Normalize the guidance response (columnar object OR row array) to rows. */
function rowsFromResponse(data: any): Record<string, any>[] {
  if (!data) return [];
  if (Array.isArray(data)) return data as Record<string, any>[];
  if (typeof data !== 'object') return [];

  const keys = Object.keys(data);
  const firstArrayKey = keys.find((key) => Array.isArray(data[key]));
  if (!firstArrayKey) return [];

  const rowCount = data[firstArrayKey].length;
  const rows: Record<string, any>[] = [];
  for (let index = 0; index < rowCount; index += 1) {
    const row: Record<string, any> = {};
    keys.forEach((key) => {
      row[key] = Array.isArray(data[key]) ? data[key][index] : data[key];
    });
    rows.push(row);
  }
  return rows;
}

/** Case-insensitive column read across candidate spellings. */
function field(row: Record<string, any>, ...names: string[]): string {
  const keys = Object.keys(row);
  for (const name of names) {
    const key = keys.find((candidate) => candidate.toLowerCase() === name.toLowerCase());
    if (key !== undefined && row[key] != null) {
      return String(row[key]);
    }
  }
  return '';
}

async function loadGuidanceRows(ticker: string, token: string): Promise<Record<string, any>[]> {
  const cacheKey = `${ticker.toUpperCase()}|${token}`;
  let pending = guidanceCache.get(cacheKey);
  if (!pending) {
    pending = api.consensus
      .getGuidanceData(ticker, token, USE_NEW_GUIDANCE)
      .then((response) => rowsFromResponse((response as any).data))
      .catch((error) => {
        guidanceCache.delete(cacheKey); // allow a later cell to retry
        throw error;
      });
    guidanceCache.set(cacheKey, pending);
  }
  return pending;
}

function guideValueForCategory(row: Record<string, any>, category: string): string {
  switch (category.trim().toLowerCase()) {
    case 'g':
      return field(row, 'GUIDE', 'GUIDE_TEXT');
    case 'gl':
      return field(row, 'LOW_GUIDE', 'LOW_GUIDE_TEXT');
    case 'gh':
      return field(row, 'HIGH_GUIDE', 'HIGH_GUIDE_TEXT');
    case 'gm':
      return field(row, 'MID_GUIDE', 'MID_GUIDE_TEXT');
    default:
      return '';
  }
}

async function resolveIGDValue(
  ticker: string,
  fiscalPeriod: string,
  ltCode: string,
  category: string,
  sourcePeriod: string
): Promise<any> {
  if (!ticker || ticker === '0' || ticker === '-' || ticker === 'Invalid Ticker') {
    return NULL_RETURN_VALUE;
  }

  const token = await tokenStorage.getUsernameFilter();
  if (!token) {
    return NULL_RETURN_VALUE;
  }

  const rows = await loadGuidanceRows(ticker, token);
  if (rows.length === 0) {
    return NULL_RETURN_VALUE;
  }

  // Support multiple LT codes like the COM add-in: "(a,b)" tries each until a
  // non-empty value is found.
  const ltParts = ltCode
    .replace(/[()]/g, '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  for (const part of ltParts) {
    const isInferred = part.toLowerCase().startsWith('i_');

    for (const row of rows) {
      if (field(row, 'GUIDE_FISCALPERIOD') !== fiscalPeriod) continue;

      // LTCODE match, or UIC match for i_ (inferred) codes — mirrors the COM
      // fallback `UIC = '...' when result < 1 && Lt_CodePart starts with "i_"`.
      const codeMatches =
        field(row, 'LTCODE', 'LT_CODE') === part || (isInferred && field(row, 'UIC') === part);
      if (!codeMatches) continue;

      if (sourcePeriod && field(row, 'SOURCE_FISCALPERIOD') !== sourcePeriod) continue;

      const value = guideValueForCategory(row, category);
      if (value.trim() !== '') {
        return value; // return as-is (string), matching the COM IGD return type
      }
    }
  }

  return NULL_RETURN_VALUE;
}

/**
 * InSync Guidance Data - returns a company's guidance figure for a fiscal period.
 * @customfunction IGD_OFJ
 * @helpurl https://insyncanalytics.com/consensus/add-in/functions/#igd-ofj
 * @streaming
 * @param ticker Ticker symbol.
 * @param fiscalPeriod Guidance fiscal period (e.g. 2027).
 * @param ltCode Line item / LT code (or UIC for i_ codes).
 * @param category Guidance datapoint: G, GH, GM, or GL.
 * @param {string} [sourcePeriod] Optional source fiscal period.
 * @param {CustomFunctions.StreamingInvocation<any>} invocation Streaming invocation used to update the cell.
 */
export function IGD_OFJ(
  ticker: string,
  fiscalPeriod: string = '',
  ltCode: string = '',
  category: string = 'G',
  sourcePeriod: string = '',
  invocation: CustomFunctions.StreamingInvocation<any>
): void {
  if (!invocation?.setResult) {
    return;
  }

  // Evaluating an InSync formula counts as using the add-in (OTP idle timeout).
  recordAddinActivity();

  let canceled = false;
  invocation.setResult('Loading...');
  invocation.onCanceled = () => {
    canceled = true;
  };

  resolveIGDValue(ticker, fiscalPeriod, ltCode, category, sourcePeriod)
    .then((result) => {
      if (!canceled) invocation.setResult(result);
    })
    .catch((error) => {
      logger.error('IGD streaming function error', error as Error);
      if (!canceled) invocation.setResult('-ERROR');
    });
}

CustomFunctions.associate("IGD_OFJ", IGD_OFJ);